import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Users, Gift, Copy, Share2, Sparkles, Check, Wallet } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";

export default function Referrals() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [redeeming, setRedeeming] = useState(false);

  const load = () => api.get("/referrals/me").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const redeem = async () => {
    setRedeeming(true);
    try {
      const { data: r } = await api.post("/referrals/redeem");
      if (r.applied_as === "stripe_credit") {
        toast.success(`Credito applicato al tuo account Stripe (${r.months_redeemed} ${r.months_redeemed === 1 ? "mese" : "mesi"} Premium)`);
      } else {
        toast.success(`Trial esteso di ${r.months_redeemed * 30} giorni`);
      }
      load();
    } catch (e) { toast.error(errMsg(e, "Errore")); }
    finally { setRedeeming(false); }
  };

  if (!data) return <div className="text-zen-ink2">Caricamento…</div>;

  const link = `${window.location.origin}/?ref=${data.referral_code}`;
  const rewardPct = ((data.referred_count % 3) / 3) * 100;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Link copiato!");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error("Clipboard copy failed", e);
      toast.error("Copia manuale del link");
    }
  };

  const shareNative = async () => {
    const shareData = {
      title: "FreelanZen — provalo!",
      text: "Sto usando FreelanZen per gestire il mio lavoro da freelance. Provalo con il mio invito:",
      url: link,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (e) {
        // AbortError = user cancelled — that's fine, do nothing.
        if (e?.name !== "AbortError") console.warn("Web Share failed", e);
      }
    } else {
      copyLink();
    }
  };

  return (
    <div className="space-y-8" data-testid="referrals-page">
      <div>
        <span className="zen-label">Passaparola</span>
        <h1 className="mt-2 font-heading text-3xl">Invita altri freelance</h1>
        <p className="text-zen-ink2 mt-1 max-w-xl">
          Ogni <b>3 amici</b> che si registrano usando il tuo link, tu guadagni <b>1 mese Premium omaggio</b>. Loro entrano con la piattaforma già configurata.
        </p>
      </div>

      {/* Reward card */}
      <div className="zen-card p-6 md:p-8 bg-gradient-to-br from-white to-zen-subtle/20">
        <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-2xl bg-zen-primary text-white flex items-center justify-center">
                <Gift size={22} strokeWidth={1.5}/>
              </div>
              <div>
                <div className="zen-label">La tua ricompensa</div>
                <div className="font-heading text-2xl">{data.months_earned} {data.months_earned === 1 ? "mese" : "mesi"} Premium</div>
              </div>
            </div>
            <div className="mt-6">
              <div className="flex justify-between text-sm text-zen-ink2 mb-2">
                <span>{data.referred_count % 3}/3 verso il prossimo mese gratis</span>
                <span>{data.to_next_reward} amici mancanti</span>
              </div>
              <div className="h-3 bg-zen-border rounded-full overflow-hidden">
                <div
                  className="h-full bg-zen-primary rounded-full transition-all duration-500"
                  style={{ width: `${rewardPct}%` }}
                  data-testid="reward-progress"
                />
              </div>
            </div>
          </div>
          <div className="text-center md:text-right">
            <div className="zen-label">Amici invitati</div>
            <div className="font-heading text-5xl text-zen-primary mt-1" data-testid="referred-count">{data.referred_count}</div>
          </div>
        </div>
        {data.months_available > 0 && (
          <div className="mt-6 pt-6 border-t border-zen-border flex items-center justify-between gap-4 flex-wrap" data-testid="redeem-block">
            <div className="flex items-center gap-3">
              <Wallet size={18} className="text-zen-primary"/>
              <div>
                <div className="text-sm font-medium">Hai <b>{data.months_available}</b> {data.months_available === 1 ? "mese" : "mesi"} da riscattare</div>
                <div className="text-xs text-zen-ink2">Se hai un abbonamento attivo, applicheremo un credito Stripe di € {(data.months_available * 9.99).toFixed(2).replace(".", ",")}. Altrimenti estenderemo la tua prova gratuita di {data.months_available * 30} giorni.</div>
              </div>
            </div>
            <Button data-testid="redeem-btn" onClick={redeem} disabled={redeeming} className="bg-zen-primary hover:bg-zen-primaryHover rounded-full">
              {redeeming ? "…" : "Riscatta ricompensa"}
            </Button>
          </div>
        )}
        {data.months_redeemed > 0 && data.months_available === 0 && (
          <div className="mt-4 text-xs text-zen-ink2" data-testid="redeemed-note">
            ✓ {data.months_redeemed} {data.months_redeemed === 1 ? "mese" : "mesi"} già riscattato.
          </div>
        )}
      </div>

      {/* Share block */}
      <div className="zen-card p-6 md:p-8">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={16} className="text-zen-primary"/>
          <span className="zen-label">Il tuo codice invito</span>
        </div>
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <span data-testid="referral-code" className="font-heading text-3xl md:text-4xl tracking-widest text-zen-ink px-5 py-2 rounded-xl bg-zen-subtle/25">
            {data.referral_code}
          </span>
        </div>

        <label className="zen-label">Link condivisibile</label>
        <div className="flex gap-2 mt-2 flex-wrap">
          <Input readOnly value={link} data-testid="referral-link" className="flex-1 min-w-[240px] font-mono text-sm bg-zen-bg"/>
          <Button data-testid="copy-link-btn" onClick={copyLink} variant="outline" className="rounded-full">
            {copied ? <Check size={16} className="mr-1.5"/> : <Copy size={16} className="mr-1.5"/>}
            {copied ? "Copiato" : "Copia"}
          </Button>
          <Button data-testid="share-link-btn" onClick={shareNative} className="bg-zen-primary hover:bg-zen-primaryHover rounded-full">
            <Share2 size={16} className="mr-1.5"/> Condividi
          </Button>
        </div>

        <div className="mt-6 grid sm:grid-cols-3 gap-3">
          {[
            { label: "WhatsApp", url: `https://wa.me/?text=${encodeURIComponent("Sto usando FreelanZen, provalo: " + link)}` },
            { label: "Email", url: `mailto:?subject=${encodeURIComponent("FreelanZen — provalo")}&body=${encodeURIComponent("Ciao! Ti consiglio FreelanZen per gestire il lavoro da freelance. Provalo qui: " + link)}` },
            { label: "LinkedIn", url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}` },
          ].map((s) => (
            <a key={s.label} href={s.url} target="_blank" rel="noreferrer" data-testid={`share-${s.label.toLowerCase()}`}
              className="p-3 rounded-xl border border-zen-border text-center text-sm text-zen-ink hover:bg-zen-subtle/20 transition-colors">
              Condividi su {s.label}
            </a>
          ))}
        </div>
      </div>

      {/* Referred users list */}
      <div className="zen-card p-6">
        <div className="flex items-center gap-2 mb-5">
          <Users size={18} className="text-zen-primary"/>
          <h2 className="font-heading text-xl">I tuoi invitati</h2>
        </div>
        {data.referred_users.length === 0 ? (
          <div className="py-8 text-center text-zen-ink2 text-sm">
            Nessun invito ancora. Condividi il tuo link e comincia a guadagnare mesi Premium!
          </div>
        ) : (
          <ul className="divide-y divide-zen-border">
            {data.referred_users.map((u) => (
              <li key={u.user_id} className="py-3 flex items-center gap-3" data-testid={`referred-${u.user_id}`}>
                <Avatar className="w-9 h-9">
                  <AvatarImage src={u.picture || undefined}/>
                  <AvatarFallback className="bg-zen-primary/10 text-zen-primary text-sm">
                    {(u.name || "?").split(" ").map(s=>s[0]).slice(0,2).join("").toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{u.name}</div>
                  <div className="text-xs text-zen-ink2 truncate">{u.email}</div>
                </div>
                <div className="text-xs text-zen-ink2">{fmtDate(u.created_at)}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
